import React from 'react';
import Service from './components/Service';
import Beneficiary from './components/Beneficiary';
import Description from './components/Description';
import RadioSection from './components/RadioSection';
import ImpactSummary from './components/ImpactSummary';
import CheckboxSection from './components/CheckboxSection';
import NarrativeSection from './components/NarrativeSection';
import BudgetSection from './components/BudgetSection';
import LocationSection from './components/LocationSection';
import DateSection from './components/DateSection';
import PeriodSection from './components/PeriodSection';
import ServiceCollection from './components/ServiceCollection';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FeedbackQuestion, FeedbackQuestionsById } from './api';
import { FeedbackSectionInfo } from './utils';
import { FeedbackMakeEditsHandler } from './MakeEditsModal';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
} from '../utils';
import IndicatorsCheckbox from './components/IndicatorsCheckbox';
import ImpactDetails from './components/ImpactDetails';
import CustomQuestionSection from './components/CustomQuestionSection';
import Allocation from './components/Allocation';
import { budgetQuestionIds } from '../../ReportBuilder/Components/Budget/utils';

const defaultError = (
  <TrueImpactText color="critical" align="center" className="error-text">
    This question is not implemented yet.
  </TrueImpactText>
);

interface MakeEditsQuestionProps extends Omit<FeedbackSectionInfo, 'question'> {
  questions: FeedbackQuestionsById;
  question: FeedbackQuestion | undefined;
  questionRef: React.RefObject<FeedbackMakeEditsHandler>;
}

export const MakeEditsQuestion: React.FC<MakeEditsQuestionProps> = ({
  questionRef,
  question,
  ...props
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).getQuestion = () => question;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).getQuestionId = () => question?.dbId;

  if (question === undefined) {
    return (
      <TrueImpactText variant="heading-page" align="center">
        All Done!
      </TrueImpactText>
    );
  }

  if (props.selectedSection.section.sectionId === 'custom_questions') {
    return (
      <CustomQuestionSection {...props} ref={questionRef} question={question} />
    );
  }

  if (props.selectedSection.section.sectionId === 'narrative') {
    return (
      <NarrativeSection {...props} ref={questionRef} question={question} />
    );
  }

  if (props.selectedSection.section.sectionId === 'beneficiaries') {
    return (
      <Beneficiary
        {...props}
        ref={questionRef}
        question={question}
        initialRoute="landing"
      />
    );
  }

  if (props.selectedSection.section.sectionId === 'budget') {
    const isBudgetIds = budgetQuestionIds.includes(props.questionId);
    const answer = isBudgetIds
      ? props.questions[80011]?.answers[0]?.text
      : props.answer;
    const questionId = isBudgetIds ? 80011 : props.questionId;
    return (
      <BudgetSection
        {...props}
        ref={questionRef}
        question={question}
        questionId={questionId}
        answer={answer || props.answer}
      />
    );
  }

  if (props.selectedSection.section.sectionId === 'allocation') {
    return <Allocation {...props} ref={questionRef} question={question} />;
  }

  switch (question.questionType) {
    case 'ADDRESS':
      return (
        <LocationSection {...props} ref={questionRef} question={question} />
      );

    case 'TREE':
    case 'MIXED':
      return <PeriodSection {...props} ref={questionRef} question={question} />;

    case 'DATE':
      return <DateSection {...props} ref={questionRef} question={question} />;

    case 'LONG_TEXT':
      return <Description {...props} ref={questionRef} question={question} />;

    case 'RADIO':
      if (question.dbId === IMPACT_SUMMARY_QUESTION_ID) {
        return (
          <ImpactSummary {...props} ref={questionRef} question={question} />
        );
      }
      if (
        question.dbId === INDICATOR_DETAILS_QUESTION_ID ||
        question.dbId === INDICATOR_SUMMARY_QUESTION_ID
      ) {
        return (
          <ImpactDetails {...props} ref={questionRef} question={question} />
        );
      }
      return <RadioSection {...props} ref={questionRef} question={question} />;

    case 'CHECKBOXES':
      if (question.dbId === IMPACT_SUMMARY_QUESTION_ID) {
        return (
          <ImpactSummary {...props} ref={questionRef} question={question} />
        );
      }
      if (question.dbId === 40010) {
        return (
          <IndicatorsCheckbox
            {...props}
            ref={questionRef}
            question={question}
          />
        );
      }
      return (
        <CheckboxSection {...props} ref={questionRef} question={question} />
      );

    case 'SERVICE_COLLECTION':
      return (
        <ServiceCollection {...props} ref={questionRef} question={question} />
      );

    case 'SERVICE':
    case 'BENEFICIARIES':
      return <Service {...props} ref={questionRef} question={question} />;

    case 'CUSTOM_OPTIONS':
      if (question.dbId === IMPACT_SUMMARY_QUESTION_ID) {
        return (
          <ImpactSummary {...props} ref={questionRef} question={question} />
        );
      }
      if (props.selectedSection.section.sectionId === 'activities') {
        return (
          <RadioSection {...props} ref={questionRef} question={question} />
        );
      }
      if (
        question.dbId === INDICATOR_DETAILS_QUESTION_ID ||
        question.dbId === INDICATOR_SUMMARY_QUESTION_ID
      ) {
        return (
          <ImpactDetails {...props} ref={questionRef} question={question} />
        );
      }
      return defaultError;

    default:
      return defaultError;
  }
};
